import React from "react";
import UserAuthStore from "../../store/userStore";
import { useGetSpectras } from "../../api/queryHooks";

/**
 * SparkEmptyState - Unified empty state for new Spark chats
 * 
 * Displays a centered greeting message when chat has no messages yet.
 * Supports both Spark (personal) and Spectra (workspace) contexts.
 * 
 * @param {string} variant - "spark" or "spectra" 
 * @param {string} space_id - Spectra workspace ID (for spectra variant)
 * @param {string} userName - Optional override for user's display name
 * @param {string} title - Optional override for main title
 * @param {string} description - Optional override for description text
 */
const SparkEmptyState = ({ 
  variant = "spark", 
  space_id,
  userName,
  title,
  description 
}) => {
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { data: spectrasData } = useGetSpectras(orgId, user?.uid);
  const spaces = spectrasData || [];

  // Get default values based on variant
  const getDefaultTitle = () => {
    if (title) return title;
    
    if (variant === "spectra" && space_id) {
      const currentSpace = Array.isArray(spaces)
        ? spaces.find((space) => space.id === space_id)
        : null;
      
      return currentSpace
        ? `${currentSpace.title} spectra is ready`
        : "spectra is ready.";
    }
    
    return "How can I help?";
  };

  const getDefaultDescription = () => {
    if (description) return description;
    
    if (variant === "spectra") {
      return (
        <>
          Searches in this spectra will use this set of instructions,
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          files in all sparks.
        </>
      );
    }
    
    return (
      <>
        Photon is ready to assist you with anything you need, including
        <br className="hidden sm:block" />
        <span className="sm:hidden"> </span>
        queries answered and support for your regular work tasks.
      </>
    );
  };

  const displayName = userName || user?.displayName;
  const greeting = displayName ? `Hi, ${displayName}` : "Hi,";

  return (
    <div className="flex items-center justify-center h-[55vh] w-full px-4 sm:px-6">
      <div className="text-center">
        <h1 className="opacity-100 text-xl sm:text-xl lg:text-[22px] mb-2 sm:mb-3 font-semibold greeting-txt-gradient">
          {greeting}
        </h1>

        <h2 className={`dark:text-ink-white text-ink-ink opacity-100 text-lg lg:text-xl mb-3 font-bold ${
          variant === "spectra" ? "word-break-normal" : "whitespace-nowrap"
        }`}>
          {getDefaultTitle()}
        </h2>

        <p className="dark:text-ink-copy-hover text-ink-control-border opacity-100 text-[13px] xm:text-sm mx-auto">
          {getDefaultDescription()}
        </p>
      </div>
    </div>
  );
};

export default SparkEmptyState;
